import React, { useState } from 'react';

/**
 * EditableBlock - Wrapper réutilisable pour les sections en mode "Inline Editing"
 *
 * Props:
 *  - title: string ou JSX (titre du bloc)
 *  - icon: nom d'icône bootstrap (ex: "bi-person-vcard")
 *  - extraHeader: JSX additionnel à droite du titre (badge, etc.)
 *  - renderView: ({ openEdit }) => JSX (mode consultation)
 *  - renderEdit: ({ closeEdit, save }) => JSX (mode édition - doit appeler save() pour persister)
 *  - onSave: async (data) => void (handler de sauvegarde, reçoit ce que renderEdit passe à save())
 *  - className: string (classes additionnelles)
 *  - testid: string (data-testid)
 */
const EditableBlock = ({
  title,
  icon = 'bi-square',
  extraHeader = null,
  renderView,
  renderEdit,
  onSave,
  className = '',
  testid,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const openEdit = () => {
    setError(null);
    setIsEditing(true);
  };

  const closeEdit = () => {
    setError(null);
    setIsEditing(false);
  };

  const save = async (data) => {
    if (!onSave) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(data);
      setIsEditing(false);
    } catch (err) {
      console.error('[EditableBlock] save error:', err);
      setError(err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid={testid}
      className={`bg-white rounded-xl shadow-sm border ${
        isEditing ? 'border-blue-300 ring-2 ring-blue-200' : 'border-slate-200'
      } p-5 transition-all ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
          <i className={`bi ${icon} text-slate-400`}></i>
          {title}
          {isEditing && (
            <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase">
              En édition
            </span>
          )}
          {extraHeader}
        </h2>
        {!isEditing && (
          <button
            type="button"
            onClick={openEdit}
            data-testid={testid ? `${testid}-edit-btn` : undefined}
            className="w-9 h-9 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-[#007BFF] flex items-center justify-center border border-slate-200 hover:border-blue-300 transition"
            title="Modifier"
          >
            <i className="bi bi-pencil-square"></i>
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          {renderEdit({ closeEdit, save, saving })}
          {error && (
            <div className="mt-3 p-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-xs">
              <i className="bi bi-exclamation-circle mr-1"></i>
              {error}
            </div>
          )}
        </div>
      ) : (
        renderView({ openEdit })
      )}
    </section>
  );
};

/**
 * EditableBlockFooter - Footer standard pour les modes édition (Annuler + Enregistrer)
 */
export const EditableBlockFooter = ({ onCancel, onSave, saving, saveLabel = 'Enregistrer', testid }) => (
  <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-slate-100">
    <button
      type="button"
      onClick={onCancel}
      disabled={saving}
      data-testid={testid ? `${testid}-cancel` : undefined}
      className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50"
    >
      Annuler
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      data-testid={testid ? `${testid}-save` : undefined}
      className="px-4 py-2 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
    >
      {saving ? (
        <>
          <i className="bi bi-arrow-repeat animate-spin"></i>
          Enregistrement…
        </>
      ) : (
        <>
          <i className="bi bi-check-lg"></i>
          {saveLabel}
        </>
      )}
    </button>
  </div>
);

export default EditableBlock;
